/* eslint-disable no-restricted-syntax */
/* eslint-disable no-plusplus */
import lod_ from "lodash";
import { LoadingComplexStatisticsCard } from "components/Advanced/Cards/StatisticsCards/ComplexStatisticsCard";
import GradientLineChart from "components/Advanced/Charts/LineCharts/GradientLineChart";
import DataTable from "components/Advanced/Tables/DataTable";
import StackedChart from "components/Advanced/Charts/StackedChart";
import ListOptions from "components/Custom/ListOptions";
import DefaultLineChart from "components/Advanced/Charts/LineCharts/DefaultLineChart";

import {
	Card,
	Icon,
	GridLegacy as Grid,
	Skeleton,
	Fade,
	FormControl,
	InputLabel,
	Select,
	MenuItem
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { t } from "i18next";
import { useEffect, useState } from "react";

function CrossTable({ chart, data: dataset, ...rest }) {
	const chartCollection = chart?.collections || [];
	const chartDetails = chartCollection.filter(c => !lod_.isNil(c.detail)).map(c => c.detail);
	const chartDetailsFields = chartDetails.map(c => c.field) || [];

	const { editMode = false } = chart;

	const [mode, setMode] = useState("percentage");
	const [data, setData] = useState(null);

	/**
	 * Handle change of dataset mode
	 * @param {string} newMode - the new mode to set for the dataset
	 */
	const handleChangeDatasetMode = (newMode = mode) => {
		const rows = lod_.cloneDeep(dataset).rows.map(row => {
			for (let detail of chartDetailsFields) {
				let detailValue = {};

				const value = lod_.get(row, detail);

				if (!lod_.isNil(value)) {
					const possibleValue = lod_.get(value, newMode);
					if (!lod_.isNil(possibleValue)) {
						detailValue = possibleValue;
					}
				}

				lod_.merge(row, detailValue);
				delete row[detail];
			}
			return row;
		});

		setMode(newMode);
		setData({ ...dataset, rows });
	};

	function mapData(chart, data) {
		let skeleton;

		switch (chart.display.subType) {
			case "line":
			case "line2":
			case "stackedBar":
				skeleton = {
					labels: [],
					datasets: [],
					options: chart.options
				};
				/**
				 * Labels from columns headers
				 */
				for (let i = 1; i <= data.columns.length; i++) {
					if (data.columns[i]) {
						skeleton.labels.push(data.columns[i].Header);
					}
				}
				/**
				 * Dataset from rows
				 */
				for (let i = 0; i <= data.rows.length; i++) {
					let actualRow = data.rows[i];

					if (actualRow) {
						// Create dataset object
						let obj = {
							label: actualRow.label?.toString() ?? actualRow.row?.toString(),
							color: actualRow.color ?? "info",
							data: []
						};
						// Get data from columns header accessor
						for (let j = 1; j <= data.columns.length - 1; j++) {
							let { accessor } = data.columns[j];

							let value = actualRow[accessor] ?? 0;

							obj.data.push(value);
						}
						if (lod_.isEmpty(obj.color)) {
							obj.color = "#1A73E8";
						}
						// Push dataset to datasets array
						skeleton.datasets.push(obj);
					}
				}
				skeleton.datasets.sort((a, b) => (a.label > b.label ? 1 : -1));
				return skeleton;
			default:
				return data;
		}
	}

	useEffect(() => {
		setData(lod_.cloneDeep(dataset));
		if (!lod_.isEmpty(chartDetailsFields) && !lod_.isNil(dataset)) {
			handleChangeDatasetMode(mode);
		}
	}, [chart, dataset]);

	if (!chart.loaded) {
		return (
			<Fade in={!chart.loaded}>
				<Grid item {...rest} sx={{ mt: 0, p: 0 }}>
					<MDBox py={2} pr={2} pl={chart.display.icon.component ? 1 : 2}>
						<Skeleton variant="rounded" height={500} />
					</MDBox>
				</Grid>
			</Fade>
		);
	} else if (!data) {
		return (
			<Grid item {...rest}>
				<MDBox mb={1.5}>
					<LoadingComplexStatisticsCard
						icon={chart.display.icon}
						title={chart.display.title}
						text={data?.rows?.length === 0 ? "Aucune donnée" : "Chargement des données"}
					/>
				</MDBox>
			</Grid>
		);
	} else {
		switch (chart.display.subType) {
			case "table":
			case "sortedList": {
				let props = {};

				if (!lod_.isNil(chart.pagination)) {
					props.entriesPerPage = chart.pagination;
				}

				let hasLimit = Boolean(chart.request.limit !== "null");

				if (chart.display.subType === "sortedList") {
					if (chart.request.limit !== "null") {
						props = {
							entriesPerPage: {
								defaultValue: hasLimit ? chart.request.limit : 25
							},
							showTotalEntries: false,
							fixedList: hasLimit
						};
					}
				}

				return (
					<Grid item {...rest}>
						<Card>
							<MDBox py={2} pr={2} pl={chart.display.icon?.component ? 1 : 2}>
								<MDBox
									display="flex"
									justifyContent="space-between"
									px={chart.display.description ? 1 : 0}
									pt={chart.display.description ? 1 : 0}
								>
									<MDBox display="flex">
										{chart.display.icon.component && (
											<MDBox
												width="4rem"
												height="4rem"
												bgColor={chart.display.icon.color || "info"}
												variant="gradient"
												coloredShadow={chart.display.icon.color || "info"}
												borderRadius="xl"
												display="flex"
												justifyContent="center"
												alignItems="center"
												color="white"
												mt={-5}
												mr={2}
											>
												<Icon fontSize="medium">{chart.display.icon.component}</Icon>
											</MDBox>
										)}
										<MDBox mt={chart.display.icon.component ? -2 : 0}>
											{chart.display.title && (
												<MDTypography variant="h6">{chart.display.title}</MDTypography>
											)}
											<MDBox mb={2}>
												<MDTypography component="div" variant="button" color="text">
													{chart.display.description}
												</MDTypography>
											</MDBox>
										</MDBox>
									</MDBox>

									{!editMode && (
										<MDBox mt={chart.display.icon.component ? -2 : 0}>
											{/* Display mode */}
											{!lod_.isEmpty(chartDetailsFields) && (
												<FormControl sx={{ width: "10vw" }}>
													<InputLabel id="select-label">
														{t("CHARTS.COMPARISON.display")}
													</InputLabel>
													<Select
														labelId="select-label"
														id="select"
														label={t("CHARTS.COMPARISON.display")}
														value={mode}
														onChange={e => handleChangeDatasetMode(e.target.value)}
													>
														<MenuItem value="raw">Nombre</MenuItem>
														<MenuItem value="percentage">
															{t("CHARTS.COMPARISON.percentage")}
														</MenuItem>
													</Select>
												</FormControl>
											)}
											{/* Menu Options */}
											<ListOptions chart={chart} options={chart.options ?? {}} />
										</MDBox>
									)}
								</MDBox>
								<MDBox>
									<DataTable
										table={data}
										chart={chart}
										canSearch={chart.display.subType !== "sortedList"}
										{...props}
										mode={mode}
									/>
								</MDBox>
							</MDBox>
						</Card>
					</Grid>
				);
			}
			case "line":
				return (
					<Grid item {...rest}>
						<MDBox>
							<GradientLineChart
								icon={chart.display.icon}
								title={chart.display.title}
								description={chart.display.description}
								chartData={mapData(chart, data)}
								chart={chart}
							/>
						</MDBox>
					</Grid>
				);
			case "line2":
				return (
					<Grid item {...rest}>
						<MDBox>
							<DefaultLineChart
								icon={chart.display.icon}
								title={chart.display.title}
								description={chart.display.description}
								chartData={mapData(chart, data)}
								chart={chart}
								tableData={lod_.cloneDeep(data)}
							/>
						</MDBox>
					</Grid>
				);
			case "stackedBar":
				return (
					<Grid item {...rest}>
						<MDBox>
							<StackedChart
								icon={chart.display.icon}
								title={chart.display.title}
								description={chart.display.description}
								chartData={mapData(chart, data)}
								chart={chart}
							/>
						</MDBox>
					</Grid>
				);
			default:
				break;
		}
	}
}

export default CrossTable;
